import React from 'react';
import { toast } from 'react-toastify';
import { LockOutlined, UnlockOutlined, EyeOutlined } from '@ant-design/icons';
import { LockSongPosition } from '../../utils';
import PlanToast from '../../components/PlanToast/PlanToast';
import { PlaylistStyle1 } from './Playlist.style1';

interface PlaylistSongDropdownProps {
  data: any;
  loginState: any;
  scheduleData: any;
  playlistIdRef: React.RefObject<string | undefined>;
  setEditType: (type: string) => void;
  setEditAddToPlaylistModal: (visible: boolean) => void;
  setViewDetailData: (data: any) => void;
  showModalDeleteFix: (row: any, callback: () => void) => void;
  setShareSongModal: (visible: boolean) => void;
  handleItemDelete: (row: any) => void;
  onLockPositionSuccess?: (songUri: string, isLocked: boolean, position: number, playId: string) => void;
}

const PlaylistSongDropdown: React.FC<PlaylistSongDropdownProps> = ({
  data,
  loginState,
  scheduleData,
  playlistIdRef,
  setEditType,
  setEditAddToPlaylistModal,
  setViewDetailData,
  showModalDeleteFix,
  setShareSongModal,
  handleItemDelete,
  onLockPositionSuccess,
}) => {
  return (
    <PlaylistStyle1>
      <div style={{ position: 'relative' }}>
        <PlanToast available={['pro', 'unlimited']} titleHeading={'Edit end date'} placement="left">
          <div
            className="playListMenu"
            onClick={(e) => {
              e.stopPropagation();
              setEditType('editDate');
              setEditAddToPlaylistModal(true);
              setViewDetailData({
                ...data,
                playlsit: playlistIdRef?.current,
              });
            }}
          >
            <div className="playListMenuSvg">
              <svg
                width="18"
                height="19"
                viewBox="0 0 18 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.52988 13.7188C4.56504 13.7188 4.6002 13.7152 4.63535 13.71L7.59199 13.1914C7.62715 13.1844 7.66055 13.1686 7.68516 13.1422L15.1365 5.69082C15.1528 5.67456 15.1657 5.65524 15.1746 5.63398C15.1834 5.61271 15.1879 5.58992 15.1879 5.56689C15.1879 5.54387 15.1834 5.52108 15.1746 5.49981C15.1657 5.47855 15.1528 5.45923 15.1365 5.44297L12.215 2.51973C12.1816 2.48633 12.1377 2.46875 12.0902 2.46875C12.0428 2.46875 11.9988 2.48633 11.9654 2.51973L4.51406 9.97109C4.4877 9.99746 4.47187 10.0291 4.46484 10.0643L3.94629 13.0209C3.92919 13.1151 3.9353 13.212 3.96409 13.3033C3.99288 13.3945 4.04349 13.4774 4.11152 13.5447C4.22754 13.6572 4.37344 13.7188 4.52988 13.7188ZM5.71465 10.6531L12.0902 4.2793L13.3787 5.56777L7.00312 11.9416L5.44043 12.2176L5.71465 10.6531ZM15.4688 15.1953H2.53125C2.22012 15.1953 1.96875 15.4467 1.96875 15.7578V16.3906C1.96875 16.468 2.03203 16.5312 2.10938 16.5312H15.8906C15.968 16.5312 16.0312 16.468 16.0312 16.3906V15.7578C16.0312 15.4467 15.7799 15.1953 15.4688 15.1953Z"
                  fill="white"
                />
              </svg>
            </div>
            <p className="playListMenuText">Edit end date</p>
          </div>
        </PlanToast>
      </div>
      <div style={{ position: 'relative' }}>
        <PlanToast available={['unlimited']} titleHeading={'Reset position'} placement="left">
          <div
            className="playListMenu"
            onClick={(e) => {
              e.stopPropagation();
              let postionnumber = data.latestindex;
              if (data?.lockPosition) {
                LockSongPosition({
                  access_token: loginState.refreshToken,
                  lock: data?.lockPosition ? false : true,
                  play_id: data.playId,
                  playlist_id: playlistIdRef?.current,
                  position: postionnumber,
                  song_uri: data.songUri,
                  user_id: loginState.userId,
                }).then(() => {
                  toast.success(
                    data?.lockPosition
                      ? `The locked position has been removed. `
                      : `The song has been locked on position ${postionnumber + 1} `
                  );
                  onLockPositionSuccess?.(data.songUri, !data.lockPosition, postionnumber, data.playId);
                });
              } else {
                let exists =
                  scheduleData &&
                  // @ts-ignore
                  scheduleData?.filter(
                    obj => Number(obj.position) === Number(postionnumber) && obj.lockPosition
                  );
                if (exists.length) {
                  showModalDeleteFix(exists[0], async () => {
                    // For position conflicts: unlock existing song first, then lock new song
                    try {
                      // 1. Unlock the existing song at this position
                      await LockSongPosition({
                        access_token: loginState.refreshToken,
                        lock: false, // unlock
                        play_id: exists[0]._id, // existing song's play_id
                        playlist_id: playlistIdRef?.current,
                        position: exists[0].position, // existing position
                        song_uri: exists[0].song_uri, // existing song
                        user_id: loginState.userId,
                      });

                      // 2. Lock the new song at this position
                      await LockSongPosition({
                        access_token: loginState.refreshToken,
                        lock: true, // lock
                        play_id: data.playId,
                        playlist_id: playlistIdRef?.current,
                        position: postionnumber,
                        song_uri: data.songUri,
                        user_id: loginState.userId,
                      });

                      toast.success(`The song has been locked on position ${postionnumber + 1}`);
                      window.location.reload();
                    } catch (error) {
                      console.error('Error resolving position conflict:', error);
                      toast.error('Failed to resolve position conflict');
                    }
                  });
                } else {
                  LockSongPosition({
                    access_token: loginState.refreshToken,
                    lock: data?.lockPosition ? false : true,
                    play_id: data.playId,
                    playlist_id: playlistIdRef?.current,
                    position: postionnumber,
                    song_uri: data.songUri,
                    user_id: loginState.userId,
                  }).then(() => {
                    toast.success(
                      data?.lockPosition
                        ? `The locked position has been removed. `
                        : `The song has been locked on position ${postionnumber + 1} `
                    );
                    onLockPositionSuccess?.(data.songUri, !data.lockPosition, postionnumber, data.playId);
                  });
                }
              }
            }}
          >
            <div className="playListMenuSvg" style={{ display: 'flex', alignItems: 'center' }}>
              {data.lockPosition ? (
                <LockOutlined style={{ fontSize: 18, color: 'white' }} />
              ) : (
                <UnlockOutlined style={{ fontSize: 18, color: 'white' }} />
              )}
            </div>
            <p className="playListMenuText">
              {data.lockPosition ? 'Unlock song position' : 'Lock song position'}
            </p>
          </div>
        </PlanToast>
      </div>

      <div style={{ position: 'relative' }}>
        <PlanToast available={['pro', 'unlimited']} titleHeading={'Edit notes'} placement="left">
          <div
            className="playListMenu"
            onClick={(e) => {
              e.stopPropagation();
              setEditType('addNote');
              setEditAddToPlaylistModal(true);
              setViewDetailData({
                ...data,
                playlsit: playlistIdRef?.current,
              });
            }}
          >
            <div className="playListMenuSvg">
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 10.5H6C5.80109 10.5 5.61032 10.579 5.46967 10.7197C5.32902 10.8603 5.25 11.0511 5.25 11.25C5.25 11.4489 5.32902 11.6397 5.46967 11.7803C5.61032 11.921 5.80109 12 6 12H12C12.1989 12 12.3897 11.921 12.5303 11.7803C12.671 11.6397 12.75 11.4489 12.75 11.25C12.75 11.0511 12.671 10.8603 12.5303 10.7197C12.3897 10.579 12.1989 10.5 12 10.5ZM12 7.5H7.5C7.30109 7.5 7.11032 7.57902 6.96967 7.71967C6.82902 7.86032 6.75 8.05109 6.75 8.25C6.75 8.44891 6.82902 8.63968 6.96967 8.78033C7.11032 8.92098 7.30109 9 7.5 9H12C12.1989 9 12.3897 8.92098 12.5303 8.78033C12.671 8.63968 12.75 8.44891 12.75 8.25C12.75 8.05109 12.671 7.86032 12.5303 7.71967C12.3897 7.57902 12.1989 7.5 12 7.5ZM15 3H12.75V2.25C12.75 2.05109 12.671 1.86032 12.5303 1.71967C12.3897 1.57902 12.1989 1.5 12 1.5C11.8011 1.5 11.6103 1.57902 11.4697 1.71967C11.329 1.86032 11.25 2.05109 11.25 2.25V3H9.75V2.25C9.75 2.05109 9.67098 1.86032 9.53033 1.71967C9.38968 1.57902 9.19891 1.5 9 1.5C8.80109 1.5 8.61032 1.57902 8.46967 1.71967C8.32902 1.86032 8.25 2.05109 8.25 2.25V3H6.75V2.25C6.75 2.05109 6.67098 1.86032 6.53033 1.71967C6.38968 1.57902 6.19891 1.5 6 1.5C5.80109 1.5 5.61032 1.57902 5.46967 1.71967C5.32902 1.86032 5.25 2.05109 5.25 2.25V3H3C2.80109 3 2.61032 3.07902 2.46967 3.21967C2.32902 3.36032 2.25 3.55109 2.25 3.75V14.25C2.25 14.8467 2.48705 15.419 2.90901 15.841C3.33097 16.2629 3.90326 16.5 4.5 16.5H13.5C14.0967 16.5 14.669 16.2629 15.091 15.841C15.5129 15.419 15.75 14.8467 15.75 14.25V3.75C15.75 3.55109 15.671 3.36032 15.5303 3.21967C15.3897 3.07902 15.1989 3 15 3ZM14.25 14.25C14.25 14.4489 14.171 14.6397 14.0303 14.7803C13.8897 14.921 13.6989 15 13.5 15H4.5C4.30109 15 4.11032 14.921 3.96967 14.7803C3.82902 14.6397 3.75 14.4489 3.75 14.25V4.5H5.25V5.25C5.25 5.44891 5.32902 5.63968 5.46967 5.78033C5.61032 5.92098 5.80109 6 6 6C6.19891 6 6.38968 5.92098 6.53033 5.78033C6.67098 5.63968 6.75 5.44891 6.75 5.25V4.5H8.25V5.25C8.25 5.44891 8.32902 5.63968 8.46967 5.78033C8.61032 5.92098 8.80109 6 9 6C9.19891 6 9.38968 5.92098 9.53033 5.78033C9.67098 5.63968 9.75 5.44891 9.75 5.25V4.5H11.25V5.25C11.25 5.44891 11.329 5.63968 11.4697 5.78033C11.6103 5.92098 11.8011 6 12 6C12.1989 6 12.3897 5.92098 12.5303 5.78033C12.671 5.63968 12.75 5.44891 12.75 5.25V4.5H14.25V14.25Z"
                  fill="white"
                />
              </svg>
            </div>
            <p className="playListMenuText">{data.notes ? 'Edit' : 'Add'} notes</p>
          </div>
        </PlanToast>
      </div>
      <div
        className="playListMenu"
        onClick={(e) => {
          e.stopPropagation();
          setEditType('changePlayList');
          setEditAddToPlaylistModal(true);
          let postionnumber = data.latestindex;
          setViewDetailData({
            ...data,
            position: postionnumber,
            playlsit: playlistIdRef?.current,
          });
        }}
      >
        <div className="playListMenuSvg">
          <svg
            width="18"
            height="19"
            viewBox="0 0 18 19"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.875 8.65358C2.68512 8.65377 2.50034 8.5931 2.34861 8.48077C2.19687 8.36843 2.08643 8.21053 2.03399 8.03095C1.98156 7.85136 1.98997 7.65985 2.05796 7.48539C2.12595 7.31092 2.24982 7.16299 2.41084 7.06396L4.76049 4.75178C4.92451 4.59049 5.14693 4.49992 5.37881 4.5C5.61069 4.50008 5.83304 4.5908 5.99694 4.75221C6.16085 4.91361 6.25288 5.13248 6.2528 5.36066C6.25272 5.58884 6.16053 5.80765 5.99651 5.96894L5.01661 6.93235H11.6163C11.8481 6.93235 12.0704 7.02297 12.2344 7.18429C12.3983 7.34561 12.4904 7.5644 12.4904 7.79253C12.4904 8.02067 12.3983 8.23946 12.2344 8.40078C12.0704 8.56209 11.8481 8.65272 11.6163 8.65272H2.875V8.65358ZM15.125 10.3464C15.3149 10.3462 15.4997 10.4069 15.6514 10.5192C15.8031 10.6316 15.9136 10.7895 15.966 10.9691C16.0184 11.1486 16.01 11.3401 15.942 11.5146C15.874 11.6891 15.7502 11.837 15.5892 11.936L13.2395 14.2482C13.1583 14.3281 13.0619 14.3914 12.9558 14.4346C12.8497 14.4778 12.736 14.5 12.6212 14.5C12.5064 14.5 12.3927 14.4777 12.2866 14.4344C12.1806 14.3911 12.0842 14.3277 12.0031 14.2478C11.9219 14.1679 11.8575 14.073 11.8136 13.9686C11.7697 13.8642 11.7472 13.7523 11.7472 13.6393C11.7472 13.5264 11.7699 13.4145 11.8139 13.3101C11.8578 13.2057 11.9223 13.1109 12.0035 13.0311L12.9834 12.0677H6.38374C6.15191 12.0677 5.92957 11.977 5.76564 11.8157C5.60171 11.6544 5.50962 11.4356 5.50962 11.2075C5.50962 10.9793 5.60171 10.7605 5.76564 10.5992C5.92957 10.4379 6.15191 10.3473 6.38374 10.3473H15.125V10.3464Z"
              fill="white"
            />
          </svg>
        </div>
        <p className="playListMenuText">Move song</p>
      </div>

      <div className="playListMenu">
        <div className="playListMenuSvg" style={{ display: 'flex', alignItems: 'center' }}>
          <EyeOutlined style={{ fontSize: 18, color: 'white' }} />
        </div>
        <p
          className="playListMenuText"
          onClick={(e) => {
            e.stopPropagation();
            setEditType('viewDetail');
            setEditAddToPlaylistModal(true);
            setViewDetailData({
              ...data,
              playlsit: playlistIdRef?.current,
            });
          }}
        >
          View song details
        </p>
      </div>
      <div className="playListMenu">
        <div className="playListMenuSvg">
          <svg
            style={{ width: '17px' }}
            width="15"
            height="15"
            viewBox="0 0 16 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.1666 3.5L7.66658 1M7.66658 1L5.16658 3.5M7.66658 1V9.33334M11.8331 6.83334C12.6096 6.83334 12.9979 6.83334 13.3042 6.96017C13.7126 7.12934 14.0372 7.45401 14.2064 7.86242C14.3333 8.16867 14.3333 8.55676 14.3333 9.33334V13.3333C14.3333 14.2668 14.3333 14.7332 14.1516 15.0897C13.9918 15.4033 13.737 15.6588 13.4234 15.8185C13.0673 16 12.6008 16 11.6692 16H3.66358C2.73199 16 2.26616 16 1.90999 15.8185C1.59641 15.6588 1.34116 15.4033 1.18141 15.0897C0.999908 14.7335 0.999908 14.2675 0.999908 13.3359V9.33334C0.999908 8.55676 0.999991 8.16867 1.12682 7.86242C1.29599 7.45401 1.62041 7.12934 2.02874 6.96017C2.33508 6.83334 2.72333 6.83334 3.49991 6.83334"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <p
          className="playListMenuText"
          onClick={(e) => {
            e.stopPropagation();
            let postionnumber = data.latestindex + 1;

            setEditType('viewDetail');
            setViewDetailData({
              ...data,
              playlsit: playlistIdRef?.current,
              position: postionnumber,
            });
            setShareSongModal(true);
          }}
        >
          Share song details
        </p>
      </div>
      <div
        className="playListMenu"
        onClick={(e) => {
          e.stopPropagation();
          let postionnumber = data.latestindex;
          setEditType('delete');
          handleItemDelete({ ...data, position: postionnumber });
        }}
      >
        <div className="playListMenuSvg">
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.875 3.375H10.125C10.125 3.07663 10.0065 2.79048 9.7955 2.5795C9.58452 2.36853 9.29837 2.25 9 2.25C8.70163 2.25 8.41548 2.36853 8.2045 2.5795C7.99353 2.79048 7.875 3.07663 7.875 3.375ZM6.75 3.375C6.75 2.77826 6.98705 2.20597 7.40901 1.78401C7.83097 1.36205 8.40326 1.125 9 1.125C9.59674 1.125 10.169 1.36205 10.591 1.78401C11.0129 2.20597 11.25 2.77826 11.25 3.375H15.75C15.8992 3.375 16.0423 3.43426 16.1477 3.53975C16.2532 3.64524 16.3125 3.78832 16.3125 3.9375C16.3125 4.08668 16.2532 4.22976 16.1477 4.33525C16.0423 4.44074 15.8992 4.5 15.75 4.5H15.1155L13.7599 14.4427C13.6679 15.1165 13.335 15.7342 12.8226 16.1814C12.3103 16.6286 11.6533 16.875 10.9732 16.875H7.02675C6.34671 16.875 5.6897 16.6286 5.17736 16.1814C4.66503 15.7342 4.33207 15.1165 4.24013 14.4427L2.8845 4.5H2.25C2.10082 4.5 1.95774 4.44074 1.85225 4.33525C1.74676 4.22976 1.6875 4.08668 1.6875 3.9375C1.6875 3.78832 1.74676 3.64524 1.85225 3.53975C1.95774 3.43426 2.10082 3.375 2.25 3.375H6.75ZM7.875 7.3125C7.875 7.16332 7.81574 7.02024 7.71025 6.91475C7.60476 6.80926 7.46168 6.75 7.3125 6.75C7.16332 6.75 7.02024 6.80926 6.91475 6.91475C6.80926 7.02024 6.75 7.16332 6.75 7.3125V12.9375C6.75 13.0867 6.80926 13.2298 6.91475 13.3352C7.02024 13.4407 7.16332 13.5 7.3125 13.5C7.46168 13.5 7.60476 13.4407 7.71025 13.3352C7.81574 13.2298 7.875 13.0867 7.875 12.9375V7.3125ZM10.6875 6.75C10.8367 6.75 10.9798 6.80926 11.0852 6.91475C11.1907 7.02024 11.25 7.16332 11.25 7.3125V12.9375C11.25 13.0867 11.1907 13.2298 11.0852 13.3352C10.9798 13.4407 10.8367 13.5 10.6875 13.5C10.5383 13.5 10.3952 13.4407 10.2898 13.3352C10.1843 13.2298 10.125 13.0867 10.125 12.9375V7.3125C10.125 7.16332 10.1843 7.02024 10.2898 6.91475C10.3952 6.80926 10.5383 6.75 10.6875 6.75ZM5.355 14.2909C5.41021 14.6951 5.60998 15.0656 5.91732 15.3338C6.22467 15.6021 6.6188 15.7499 7.02675 15.75H10.9732C11.3814 15.7502 11.7758 15.6025 12.0834 15.3342C12.391 15.0659 12.5909 14.6953 12.6461 14.2909L13.9804 4.5H4.01962L5.355 14.2909Z"
              fill="white"
            />
          </svg>
        </div>
        <p className="playListMenuText">Delete this song</p>
      </div>
    </PlaylistStyle1>
  );
};

export default PlaylistSongDropdown;
